/**
 * Tous les textes visibles de l'interface client.
 *
 * Aucun texte visible ne doit etre ecrit en dur dans un composant.
 * Messages de reference : docs/15-ui-ux.md, section 7.
 * Libelles de statut : docs/13-glossaire.md, section 4.
 */

import type { CustomerOrderStatus } from '@scan2eat/shared';

export const labels = {
  common: {
    back: 'Revenir',
  },

  nav: {
    label: 'Navigation',
    menu: 'Menu',
    cart: 'Ma commande',
    orders: 'Suivi',
  },

  resolve: {
    title: 'Ouverture du menu',
    text: 'Nous préparons la carte de votre table.',
    errorTitle: 'Impossible d\'ouvrir le menu',
    scanAgain: 'Scanner un autre QR code',
  },

  app: {
    name: 'Scan2Eat',
    tagline: 'Votre restaurant, à portée de QR code.',
    tableLabel: (label: string) => `Table ${label}`,
    rescan: 'Rescanner le QR',
  },

  /** Landing page, route `/`. See docs/15-ui-ux.md, section 3.1 bis. */
  vitrine: {
    nav: {
      label: 'Navigation principale',
      features: 'Fonctionnalités',
      howItWorks: 'Comment ça marche',
      dashboard: 'Tableau de bord',
      restaurantSpace: 'Espace restaurant',
      scan: 'Scanner un QR code',
    },
    hero: {
      videoLabel: 'Une cliente scanne le QR code posé sur sa table, puis son plat arrive.',
      scrollHint: 'Faites défiler',
      progressLabel: 'Progression de la présentation',
      chapters: [
        {
          step: 'Scan2Eat',
          title: 'Votre restaurant, à portée de QR code.',
          text: 'Gérez vos commandes, vos menus et la présentation de votre restaurant, simplement.',
        },
        {
          step: '01 · Scanner',
          title: 'Le client scanne. Le menu s\'ouvre.',
          text: 'Aucune application à installer, aucun compte à créer : le QR code de la table suffit.',
        },
        {
          step: '02 · Commander',
          title: 'La commande part en cuisine.',
          text: 'Elle apparaît à l\'instant sur l\'écran du personnel, avec la table et les notes.',
        },
        {
          step: '03 · Servir',
          title: 'Le plat arrive à table.',
          text: 'Le client suit l\'avancement en direct, puis règle et télécharge son reçu.',
        },
      ],
    },
    pillars: {
      eyebrow: 'Les quatre piliers',
      title: 'Tout le service, depuis un simple QR code.',
      items: [
        { key: 'qr', title: 'QR code', subtitle: 'pour vos clients', text: 'Un code par table. Le client arrive sur le menu du restaurant, sans installation.' },
        { key: 'orders', title: 'Commandes', subtitle: 'en temps réel', text: 'Nouvelles, en cours, prêtes, servies : la salle entière sur un seul tableau.' },
        { key: 'menu', title: 'Menus', subtitle: 'modifiables à tout moment', text: 'Un plat épuisé disparaît de la commande en un geste, pour toutes les tables.' },
        { key: 'report', title: 'Bilan', subtitle: 'de journée', text: 'Chiffre d\'affaires, panier moyen et plats les plus vendus, à la fermeture.' },
      ],
    },
    steps: {
      eyebrow: 'Comment ça marche',
      title: 'De la table à la cuisine, en trois gestes.',
      items: [
        { title: 'Scanner', text: 'Le client vise le QR code posé sur la table avec l\'appareil photo de son téléphone.' },
        { title: 'Commander', text: 'Il parcourt le menu, compose son panier et ajoute une note pour la cuisine.' },
        { title: 'Suivre', text: 'Reçue, en préparation, prête, servie : chaque étape s\'affiche sans recharger.' },
      ],
    },
    phone: {
      title: 'Notre menu',
      categories: ['Tout', 'Entrées', 'Plats', 'Desserts'],
      dishes: [
        { name: 'Thiébou yapp', text: 'Riz parfumé au poisson', price: '8 500 FCFA' },
        { name: 'Poulet braisé', text: 'Accompagné de plantain', price: '7 500 FCFA' },
        { name: 'Attiéké poisson', text: 'Poisson grillé, attiéké, légumes', price: '8 000 FCFA' },
      ],
      cta: 'Passer la commande',
    },
    dashboard: {
      eyebrow: 'Tableau de bord du restaurateur',
      title: 'La salle, vue du bureau.',
      text: 'Le personnel valide, lance, met en attente et termine chaque commande. Le gérant suit sa journée d\'un coup d\'oeil.',
      illustration: 'Illustration du tableau de bord, données d\'exemple.',
      kpis: [
        { label: 'Commandes du jour', value: '24', trend: '+12 %' },
        { label: 'Chiffre d\'affaires', value: '182 000 FCFA', trend: '+18 %' },
      ],
      recent: 'Commandes récentes',
      orders: [
        { number: '#0473', amount: '12 500 FCFA', status: 'preparing', label: 'En préparation' },
        { number: '#0472', amount: '18 000 FCFA', status: 'ready', label: 'Prête' },
        { number: '#0471', amount: '9 500 FCFA', status: 'served', label: 'Servie' },
      ],
    },
    cta: {
      title: 'Prêt à passer à table ?',
      text: 'Vous êtes client : scannez le QR code de votre table. Vous êtes restaurateur : ouvrez votre espace.',
      scan: 'Scanner le QR code de ma table',
      restaurant: 'Accéder à l\'espace restaurant',
    },
    footer: {
      signature: 'Votre restaurant, à portée de QR code.',
    },
  },

  scan: {
    title: 'Scannez le QR code',
    intro: 'Scannez le QR code de votre table pour accéder au menu.',
    back: 'Retour à l\'accueil',
    pointCamera: 'Placez l\'appareil photo devant le QR code de la table.',
    scanning: 'Recherche du QR code en cours…',
    starting: 'Ouverture de l\'appareil photo…',
    camera: {
      denied: 'L\'accès à l\'appareil photo est bloqué. Sur iPhone : touchez « aA » dans la barre d\'adresse, puis Réglages du site web > Appareil photo > Autoriser.',
      insecure: 'L\'appareil photo ne s\'ouvre que sur une adresse sécurisée (https).',
      unsupported: 'Ce navigateur ne donne pas accès à l\'appareil photo. Ouvrez la page dans Safari ou Chrome.',
      'not-found': 'Aucun appareil photo n\'a été trouvé sur cet appareil.',
      busy: 'L\'appareil photo est utilisé par une autre application. Fermez-la, puis réessayez.',
      unknown: 'L\'appareil photo n\'a pas pu s\'ouvrir.',
    },
    retry: 'Réessayer',
    nativeCameraTip: 'Vous pouvez aussi viser le QR code avec l\'appareil photo de votre téléphone, ou coller son lien ci-dessous.',
    manualLabel: 'Saisir le code de la table',
    manualPlaceholder: 'Collez le lien du QR code…',
    access: 'Accéder',
    invalid: 'Code non reconnu. Vérifiez le lien ou le code.',
    hint: 'Si l\'appareil photo ne fonctionne pas, demandez au personnel le lien de votre table.',
  },

  menu: {
    title: 'Menu',
    unavailable: 'Epuisé',
    emptyCategory: 'Aucun plat dans cette catégorie.',
    add: 'Ajouter',
    viewCart: 'Voir ma commande',
    welcome: 'Bienvenue chez',
    categories: 'Catégories du menu',
    empty: 'Le menu n\'est pas encore disponible. Adressez-vous au personnel.',
    dishCount: (n: number) => `${n} plat${n > 1 ? 's' : ''}`,
    itemCount: (n: number) => `${n} article${n > 1 ? 's' : ''}`,
    quantityFor: (name: string) => `Quantité de ${name}`,
    addOne: (name: string) => `Ajouter un ${name}`,
    removeOne: (name: string) => `Retirer un ${name}`,
  },

  cart: {
    title: 'Ma commande',
    emptyTitle: 'Votre commande est vide',
    browse: 'Voir le menu',
    addItemNote: 'Ajouter une précision',
    itemNotePlaceholder: 'Sans piment, bien cuit…',
    unitPrice: (price: string) => `${price} l'unité`,
    back: 'Retour au menu',
    empty: 'Votre panier est vide. Ajoutez des plats depuis le menu.',
    noteLabel: 'Note pour la cuisine',
    notePlaceholder: 'Allergies, cuisson, sans oignons…',
    itemNoteLabel: 'Note pour ce plat',
    total: 'Total',
    submit: 'Valider la commande',
    submitting: 'Envoi en cours…',
    removed: 'Plat retiré du panier.',
    undo: 'Annuler',
    removeLine: 'Retirer ce plat et recommencer',
    clearCart: 'Vider le panier',
    clearCartConfirm: 'Vider le panier ? Les plats ajoutés seront perdus.',
  },

  order: {
    tracking: 'Suivi de commande',
    live: 'En direct',
    completed: 'Terminée',
    summary: 'Récapitulatif',
    follow: 'Suivre',
    placedAt: (time: string) => `Passée à ${time}`,
    noOrdersTitle: 'Aucune commande',
    number: (n: number) => `Commande n° ${n}`,
    sent: 'Commande envoyée. Vous pouvez suivre son avancement ici.',
    addAnother: 'Passer une commande supplémentaire',
    allOrders: 'Toutes mes commandes',
    myOrders: 'Mes commandes',
    downloadReceipt: 'Télécharger le reçu',
    noOrders: "Vous n'avez pas encore passé de commande.",
    notFound: 'Cette commande est introuvable ou appartient à une autre table.',
    validate: 'Passer au paiement',
    validating: 'Validation en cours…',
  },

  /** Libellés des quatre étapes vues par le client. Voir docs/06, section 5. */
  customerStatus: {
    PENDING: 'Reçue',
    PREPARING: 'En préparation',
    READY: 'Prête',
    SERVED: 'Servie',
    CANCELLED: 'Annulée',
  } satisfies Record<CustomerOrderStatus, string>,

  customerStatusDetail: {
    PENDING: 'Votre commande a bien été reçue.',
    PREPARING: 'Votre commande est en cuisine.',
    READY: 'Votre commande va vous être apportée.',
    SERVED: 'Bon appétit.',
    CANCELLED: 'Cette commande a été annulée.',
  } satisfies Record<CustomerOrderStatus, string>,

  payment: {
    title: 'Régler ma commande',
    mobileMoneyHint: 'MTN MoMo, Moov Money, Celtiis Cash',
    counterHint: 'Espèces ou carte, auprès du personnel',
    declaredTitle: 'Paiement en cours de vérification',
    choose: 'Comment souhaitez-vous payer ?',
    mobileMoney: 'Mobile Money',
    counter: 'Au comptoir',
    provider: 'Votre opérateur',
    phone: 'Votre numéro de téléphone',
    phoneHelp: 'La demande de paiement arrivera sur ce numéro. Validez-la avec votre code secret.',
    phoneHelpManual:
      'Réglez depuis votre application Mobile Money, puis validez ici : un membre du personnel vérifiera le paiement.',
    phoneRequired: 'Saisissez le numéro à débiter.',
    reference: 'Identifiant de la transaction',
    referenceHelp: 'Recopiez la référence reçue par SMS après votre paiement.',
    referenceRequired: 'Saisissez la référence de votre transaction.',
    feeNotice: 'Frais de paiement mobile',
    totalWithFee: 'Total à payer',
    feeEstimate: 'Montant indicatif : votre opérateur applique son propre tarif.',
    submit: 'Valider mon paiement',
    submitting: 'Envoi en cours',
    openCheckout: 'Ouvrir la page de paiement',
    checkoutHelp:
      'Mode test : le paiement se règle sur la page de PayDunya, avec un compte fictif. Revenez ensuite ici, votre reçu apparaîtra tout seul.',
    pushSent:
      'Demande envoyée. Validez le paiement sur votre téléphone ; votre reçu apparaîtra ici automatiquement.',
    declaredMobile:
      'Paiement transmis. Un membre du personnel le vérifie ; votre reçu apparaîtra ici dès qu\'il sera confirmé.',
    declaredCounter:
      'Présentez-vous au comptoir pour régler. Votre reçu apparaîtra ici dès que le paiement sera confirmé.',
    rejected: 'Votre paiement n\'a pas pu être confirmé.',
    retry: 'Reprendre ma saisie',
    reference_: 'Référence',
  },

  errors: {
    thanks: 'Merci de votre visite. Scannez à nouveau le QR code de votre table pour commander.',
    sessionExpiredTitle: 'Session expirée',
    network: 'Connexion perdue. Vérifiez votre réseau, puis réessayez.',
    connectionLost: 'Connexion perdue, reconnexion en cours',
    reconnecting: 'Reconnexion en cours',
    sessionExpired:
      'Votre session a expiré. Scannez à nouveau le QR code de votre table.',
    sessionClosed: 'Votre session a été clôturée. Merci de votre visite.',
    sessionClosedByPayment:
      'Paiement enregistré. Merci de votre visite. Votre reçu reste disponible ci-dessous.',
    orderAgainAfterPayment:
      'Pour commander à nouveau, rescannez le QR code de votre table.',
    qrInvalid: 'Ce QR code est inconnu. Vérifiez que vous avez scanné le bon code.',
    tableInactive: "Cette table n'est pas en service. Adressez-vous au personnel.",
    itemsUnavailable:
      'Certains plats ne sont plus disponibles. Retirez-les pour continuer.',
    generic: 'Une erreur est survenue. Réessayez dans un instant.',
    retry: 'Réessayer',
    notFound: 'Page introuvable.',
  },
} as const;
